import React, { FC, useState, useMemo, useContext } from 'react';
import { PermMode } from '../../WithPermission';
import { Button } from '../../Button';
import { SaveModal } from '../SaveModal';
import { CancelModal } from './CancelModal';
import { SaveAsModal } from './SaveAs';
import { useAuth } from '../../../context/AuthContext';
import { PendingChangesContext } from '../../../context/PendingChangesContext';
import { MarketingProfileById, MarketingHierarchyRowData } from '../../../@types/Marketing';
import { useMarketingProfileCache } from '../../../hooks/useMarketingProfile';
import { Permissions } from '../../../@types/Permissions';

interface Props {
  profileData: MarketingProfileById;
  groupOrgData: MarketingHierarchyRowData;
  disabled?: boolean;
  isExcluded: boolean;
}

const MarketingPreferencesFooter: FC<Props> = ({ profileData, groupOrgData, disabled = false, isExcluded }) => {
  const { checkPerm } = useAuth();
  const { setIsChanged } = useContext(PendingChangesContext);
  const { invalidateProfileCache } = useMarketingProfileCache();

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showSaveAsModal, setShowSaveAsModal] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);

  const toggleCancelModal = () => setShowCancelModal((prev: boolean) => !prev);
  const toggleSaveAsModal = () => setShowSaveAsModal((prev: boolean) => !prev);
  const toggleSaveModal = () => setShowSaveModal((prev: boolean) => !prev);

  const isSaveButtonDisabled: boolean = useMemo(() => {
    const hasOneWritePerm = checkPerm(
      [
        Permissions.MarketingProfilePreferencesWrite,
        Permissions.MarketingProfileRegistrationWrite,
        Permissions.MarketingProfileRetentionWrite
      ],
      PermMode.ANY
    );
    return (
      (profileData.default
        ? !(checkPerm([Permissions.MarketingProfileBaseWrite]) && hasOneWritePerm)
        : !hasOneWritePerm) || disabled
    );
  }, [checkPerm, profileData.default, disabled]);

  const isSaveAsButtonDisabled: boolean = useMemo(() => {
    const hasAllCreatePerm: boolean = checkPerm(
      [
        Permissions.MarketingProfilePreferencesCreate,
        Permissions.MarketingProfileRegistrationCreate,
        Permissions.MarketingProfileRetentionCreate
      ],
      PermMode.ALL
    );
    return !hasAllCreatePerm || isExcluded;
  }, [checkPerm, isExcluded]);

  const onCancelChanges = () => {
    setIsChanged(false);
    invalidateProfileCache(profileData.id);
  };

  return (
    <>
      {showCancelModal && (
        <CancelModal isOpen={showCancelModal} onClose={toggleCancelModal} onCancelChanges={onCancelChanges} />
      )}
      {showSaveAsModal && (
        <SaveAsModal
          closeModal={toggleSaveAsModal}
          isOpen={showSaveAsModal}
          groupOrgData={groupOrgData}
          profileData={profileData}
        />
      )}
      {showSaveModal && (
        <SaveModal isOpen={showSaveModal} toggleSaveModal={toggleSaveModal} profileData={profileData} />
      )}
      <section
        className="flex justify-end gap-4 pt-5 p-6 px-6 sm:px-0 md:px-6 w-full bottom-0 right-0 bg-white z-30 border-t-1 border-gray-20"
        data-testid="marketing-preferences-footer"
      >
        <Button title="Cancel" kind="secondary" onClick={toggleCancelModal} disabled={disabled} />
        <Button disabled={isSaveAsButtonDisabled} title="Save As" onClick={toggleSaveAsModal} />
        <Button disabled={isSaveButtonDisabled} title="Save" onClick={toggleSaveModal} />
      </section>
    </>
  );
};

export default MarketingPreferencesFooter;
